/**
 * 长度单位换算：英寸 ⇄ 毫米。
 *
 * 领域约定（invariants §17）：尺寸是人工确认值，库里一律存毫米整数，
 * 所以单位只是**输入 / 显示的口径**，不参与计价：
 *   1) 输入侧：把用户按英寸、厘米、米写的数字换算成 mm，落库前 Math.round
 *   2) 显示侧：把库里的 mm 换算回当前输入单位，切单位时输入框里的数字跟着变
 *
 * 英寸要照顾图纸上的习惯写法：24、24.5、24 1/2、10'6"、10-6、6 in。
 */

export const MM_PER_INCH = 25.4;
export const MM_PER_FOOT = MM_PER_INCH * 12;

/** 界面可选的输入单位。存库永远是 mm。 */
export type LengthUnit = "mm" | "cm" | "m" | "in";

export const LENGTH_UNITS: readonly LengthUnit[] = ["mm", "cm", "m", "in"];

export const LENGTH_UNIT_META: Record<LengthUnit, { label: string; name: string; hint: string }> = {
  mm: { label: "mm", name: "毫米", hint: "图纸本身就是公制时用这个" },
  cm: { label: "cm", name: "厘米", hint: "1 cm = 10 mm" },
  m: { label: "m", name: "米", hint: "1 m = 1000 mm" },
  in: { label: "英寸", name: "英寸", hint: "24 / 24 1/2 / 10-6 / 10'6\"，自动换算成毫米" },
};

export const isLengthUnit = (value: unknown): value is LengthUnit =>
  typeof value === "string" && (LENGTH_UNITS as readonly string[]).includes(value);

/** 常用尺寸速查（英寸 → 毫米），给换算器当参考表 */
export const INCH_QUICK_REFERENCE: readonly number[] = [
  6, 9, 10, 12, 15, 18, 21, 24, 30, 33, 36, 39, 42, 48, 54, 60, 72, 84, 90, 96,
];

/**
 * 统一各种写法：全角数字、½ 这类分数、`″` `′` 引号、千分位逗号都归一成 ASCII。
 * NFKC 负责全角与 ½ → 1⁄2，剩下再把 U+2044 这类分数斜杠换成 "/"。
 */
function normalize(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(/[\u2044\u2215]/g, "/")
    .replace(/[″”"]/g, '"')
    .replace(/[′’‘`´]/g, "'")
    .replace(/[,\uFF0C]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const DECIMAL = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;
const FRACTION = /^([+-]?\d+)\s*\/\s*(\d+)$/;
const MIXED_FRACTION = /^([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)$/;

/** 解析 "24" / "1.5" / "1/2" / "24 1/2" 这类数字（英制图纸习惯写分数） */
export function parseNumberToken(raw: string): number | null {
  const token = normalize(raw);
  if (!token) return null;
  if (DECIMAL.test(token)) return Number(token);
  const mixed = MIXED_FRACTION.exec(token);
  if (mixed) {
    const denominator = Number(mixed[3]);
    if (denominator === 0) return null;
    return Number(mixed[1]) + Number(mixed[2]) / denominator;
  }
  const fraction = FRACTION.exec(token);
  if (fraction) {
    const denominator = Number(fraction[2]);
    return denominator === 0 ? null : Number(fraction[1]) / denominator;
  }
  return null;
}

/** 把 "10 ft 6 in" 这类文字换回符号写法，后续只认 ' 和 " */
function markify(raw: string): string {
  return normalize(raw)
    .toLowerCase()
    .replace(/\b(feet|foot|ft)\b/g, "'")
    .replace(/\b(inches|inch)\b/g, '"')
    .replace(/\bin\b/g, '"')
    .replace(/"{2,}/g, '"')
    .replace(/'{2,}/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * 把英寸写法解析成**总英寸数**（不是毫米）。
 * 支持：24 / 24.5 / 24 1/2 / 6" / 10' / 10'6" / 10-6 / 10 6 / 10 ft 6 in
 *
 * 判定顺序：带引号的最明确 → 单个数字（含分数，按英寸）→ 中间有分隔符的两个数字（按 英尺-英寸）。
 * 所以 "24 1/2" 是 24.5 英寸，"10 6" 是 10 英尺 6 英寸，两者不会互相误解。
 */
export function parseInches(raw: string): number | null {
  const text = markify(raw);
  if (!text) return null;

  if (text.includes("'")) {
    const [feetPart = "", restPart = ""] = text.split("'");
    const feet = feetPart.trim() === "" ? 0 : parseNumberToken(feetPart);
    const rest = restPart.replace(/"/g, "").trim();
    const inches = rest === "" ? 0 : parseNumberToken(rest);
    if (feet === null || inches === null) return null;
    return feet * 12 + inches;
  }

  const plain = parseNumberToken(text.replace(/"/g, " "));
  if (plain !== null) return plain;

  const dashed = /^(\d+(?:\.\d+)?)\s*[-\s]\s*(\d+(?:\.\d+)?|\d+\s*\/\s*\d+)$/.exec(text);
  if (dashed) {
    const feet = Number(dashed[1]);
    const inches = parseNumberToken(dashed[2] ?? "");
    if (inches === null) return null;
    return feet * 12 + inches;
  }

  return null;
}

export type LengthParseResult = { ok: true; mm: number } | { ok: false; error: string };

/**
 * 把「用户按 unit 写的一段长度」换算成毫米。
 * 返回的 mm 是浮点数，**落库前请用 roundToMm()**（金额与尺寸都是整数）。
 */
export function parseLength(raw: string, unit: LengthUnit): LengthParseResult {
  if (!normalize(raw)) return { ok: false, error: "请输入长度" };

  let mm: number | null;
  if (unit === "in") {
    const inches = parseInches(raw);
    mm = inches === null ? null : inches * MM_PER_INCH;
  } else {
    const perUnit = unit === "cm" ? 10 : unit === "m" ? 1000 : 1;
    const value = parseNumberToken(raw);
    mm = value === null ? null : value * perUnit;
  }

  if (mm === null || !Number.isFinite(mm)) {
    return {
      ok: false,
      error: unit === "in" ? `看不懂这个英寸写法（示例：24、24 1/2、10-6、10'6"）` : "请输入数字，例如 600",
    };
  }
  if (mm <= 0) return { ok: false, error: "长度必须大于 0" };
  return { ok: true, mm };
}

/** 换算结果落库前统一取整到毫米 */
export const roundToMm = (mm: number): number => Math.round(mm);

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * mm → 最接近的 1/16 英寸。
 * 图纸上英制尺寸通常精确到 1/16"，而且取整后「填 24 → 存 610mm → 显示回 24」能对上，
 * 不会出现输入框里变成 24.0157 这种没法看的数字。
 */
export function mmToInches(mm: number, fractionDenominator = 16): number {
  return Math.round((mm / MM_PER_INCH) * fractionDenominator) / fractionDenominator;
}

/** 英寸数 → 图纸写法："24" / "24 1/2" / "5/16" / "10 9/16" */
export function formatInches(inches: number, fractionDenominator = 16): string {
  const sign = inches < 0 ? "-" : "";
  const abs = Math.abs(inches);
  const whole = Math.floor(abs);
  const remainder = Math.round((abs - whole) * fractionDenominator);
  if (remainder === 0) return `${sign}${whole}`;
  if (remainder === fractionDenominator) return `${sign}${whole + 1}`;
  const divisor = gcd(remainder, fractionDenominator);
  const numerator = remainder / divisor;
  const denominator = fractionDenominator / divisor;
  return whole === 0 ? `${sign}${numerator}/${denominator}` : `${sign}${whole} ${numerator}/${denominator}`;
}

/** mm → 当前输入单位下的可编辑文本（英寸按 1/16 取整，跟手填的写法一致） */
export function formatLength(mm: number, unit: LengthUnit): string {
  if (unit === "in") return formatInches(mmToInches(mm));
  const divisor = unit === "cm" ? 10 : unit === "m" ? 1000 : 1;
  const value = mm / divisor;
  return String(unit === "mm" ? Math.round(value) : Number(value.toFixed(3)));
}

/** mm → 3'0" 这种英尺英寸写法（提示 / 复核用，方便直接对图纸） */
export function formatFeetInches(mm: number, fractionDenominator = 16): string {
  const totalInches = mmToInches(mm, fractionDenominator);
  const feet = Math.floor(totalInches / 12);
  const inches = Number((totalInches - feet * 12).toFixed(6));
  return `${feet}'${formatInches(inches, fractionDenominator)}"`;
}
